// Imports
// React
import { useState, useCallback } from 'react';
// Utils
import { createLogger } from '../../../utils/logger';
// Amplify
import outputs from '../../../../amplify_outputs.json';

const logger = createLogger('useEmailCapture');

interface EmailCaptureResponse {
  success: boolean;
  message?: string;
  error?: string;
}

interface UseEmailCaptureResult {
  submitEmail: (email: string, variant?: string) => Promise<boolean>;
  isLoading: boolean;
  error: string | null;
  isSuccess: boolean;
  clearError: () => void;
  reset: () => void;
}

/**
 * Custom hook for capturing email signups via the email capture API.
 *
 * Provides functionality to submit email addresses to the backend,
 * with loading states, error handling, and success tracking.
 *
 * @returns Object containing submission function and state
 */
export const useEmailCapture = (): UseEmailCaptureResult => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  /**
   * Retrieves the email capture API endpoint from amplify outputs
   */
  const getApiEndpoint = useCallback((): string => {
    const customApis = (outputs.custom?.API || {}) as Record<
      string,
      { endpoint: string; region: string; apiName: string }
    >;

    // Look for the email-capture API endpoint
    const emailCaptureApiKey = Object.keys(customApis).find(key => key.includes('email-capture'));

    if (emailCaptureApiKey) {
      const apiConfig = customApis[emailCaptureApiKey];
      return apiConfig.endpoint;
    }

    // Fallback for local development or if not yet deployed
    logger.warn('Email capture API endpoint not found in amplify_outputs.json');
    throw new Error('Email capture API is not configured. Please deploy the API first.');
  }, []);

  /**
   * Clears the error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Resets all state to initial values
   */
  const reset = useCallback(() => {
    setIsLoading(false);
    setError(null);
    setIsSuccess(false);
  }, []);

  /**
   * Submits an email address to the email capture API
   *
   * @param email - The email address to capture
   * @param variant - Optional A/B test variant identifier
   * @returns Promise resolving to true if successful, false otherwise
   */
  const submitEmail = useCallback(
    async (email: string, variant?: string): Promise<boolean> => {
      // Reset previous state
      setIsLoading(true);
      setError(null);
      setIsSuccess(false);

      try {
        // Get API endpoint
        const apiEndpoint = getApiEndpoint();
        const url = `${apiEndpoint}email-capture`;

        logger.info('Submitting email capture', {
          email: email.substring(0, 3) + '***', // Log partial email for privacy
          variant: variant || 'default',
        });

        // Make API request
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: email.trim().toLowerCase(),
            variant: variant || 'default',
          }),
        });

        // Parse response
        const data: EmailCaptureResponse = await response.json();

        // Handle response
        if (response.ok && data.success) {
          logger.info('Email captured successfully');
          setIsSuccess(true);
          setIsLoading(false);
          return true;
        } else {
          const errorMessage = data.error || 'Failed to capture email. Please try again.';
          logger.error('Email capture failed', {
            status: response.status,
            error: errorMessage,
          });
          setError(errorMessage);
          setIsLoading(false);
          return false;
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Network error. Please check your connection and try again.';

        logger.error('Email capture request failed', { error: err });
        setError(errorMessage);
        setIsLoading(false);
        return false;
      }
    },
    [getApiEndpoint]
  );

  return {
    submitEmail,
    isLoading,
    error,
    isSuccess,
    clearError,
    reset,
  };
};
